import { ApiError, apiFetch, type ApiInit } from "@/lib/api-client";
import { deriveUploadKind, getFileExtension, type UploadCategoryKind } from "@/lib/upload/kinds";
import type { AttachUploadRulesRecord } from "@/components/admin/attach-picker/types";

const uploadRulesCache = new Map<string, AttachUploadRulesRecord>();

function uploadRulesCacheKey(kind: string, module?: string) {
  return `${kind}:${module ?? ""}`;
}

export function getCachedUploadRules(kind: string, module?: string) {
  return uploadRulesCache.get(uploadRulesCacheKey(kind, module));
}

export function setCachedUploadRules(
  kind: string,
  module: string | undefined,
  rules: AttachUploadRulesRecord,
) {
  uploadRulesCache.set(uploadRulesCacheKey(kind, module), rules);
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(bytes >= 10240 ? 0 : 1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

export function formatAttachMeta(file: {
  fileSize: number;
  width: number | null;
  height: number | null;
  mimeType: string | null;
}) {
  const sizeText = formatFileSize(file.fileSize);

  if (file.width && file.height) {
    return `${file.width}×${file.height} ${sizeText}`;
  }

  return sizeText;
}

export function formatAttachDimensions(file: {
  width: number | null;
  height: number | null;
}) {
  if (file.width && file.height) {
    return `${file.width}×${file.height}`;
  }

  return "—";
}

export function isImageMime(mimeType: string | null) {
  return Boolean(mimeType?.startsWith("image/"));
}

export function canSelectAttach(
  file: { url: string; originalName: string; mimeType: string | null; fileName?: string },
  acceptExtensions?: string[],
  allowedKinds?: UploadCategoryKind[],
) {
  const extension = getFileExtension(file.fileName || file.url || file.originalName);
  const category = deriveUploadKind(extension);
  if (allowedKinds && (category === "file" || !allowedKinds.includes(category))) {
    return { ok: false as const, extensions: acceptExtensions ?? [] };
  }
  if (!acceptExtensions || acceptExtensions.length === 0) {
    return { ok: true as const };
  }

  if (!extension || !acceptExtensions.includes(extension)) {
    return { ok: false as const, extensions: acceptExtensions };
  }

  return { ok: true as const };
}

export function formatQuotaText(usedSizeBytes: number, maxSizeBytes: number) {
  return `${formatFileSize(usedSizeBytes)} / ${formatFileSize(maxSizeBytes)}`;
}

export async function attachApi<T>(
  url: string,
  init: ApiInit | undefined,
  fallbackMessage: string,
): Promise<{ ok: true; data: T } | { ok: false; message: string }> {
  try {
    return { ok: true, data: await apiFetch<T>(url, init) };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof ApiError ? error.message : fallbackMessage,
    };
  }
}
